/* eslint-disable @typescript-eslint/no-unused-vars */
'use client';

import * as React from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@radix-ui/react-icons';
import { DayPicker, useDayPicker } from 'react-day-picker';
import type { CalendarMonth } from 'react-day-picker';
import { enUS, fr, de, sk, cs } from 'date-fns/locale';
import { format } from 'date-fns';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { buttonVariants } from '@eliadmin/core/src/components/ui/button';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

const getCapitalizedMonth = (month: string) => month.charAt(0).toUpperCase() + month.slice(1);

export const localeMapping: Record<string, Locale> = {
  'en-US': enUS,
  'fr-FR': fr,
  'de-DE': de,
  sk: sk,
  'sk-SK': sk,
  cs: cs,
  'cs-CZ': cs,
};

function Calendar({ className, classNames, showOutsideDays = true, ...props }: CalendarProps) {
  const localeCode = useLocaleStore(s => s.currentLocale);
  const locale = localeMapping[localeCode] || enUS;

  // v9: MonthCaption replaces Caption; receives calendarMonth instead of displayMonth
  const CustomMonthCaption = ({
    calendarMonth,
  }: {
    calendarMonth: CalendarMonth;
    displayIndex: number;
  }) => {
    const { goToMonth } = useDayPicker();
    const displayMonth = calendarMonth.date;

    const months = React.useMemo(() => Array.from({ length: 12 }, (_, i) => new Date(0, i)), []);
    const years = React.useMemo(() => {
      const currentYear = new Date().getFullYear();
      const fromYear = (props as any).fromYear || currentYear - 10;
      const toYear = (props as any).toYear || currentYear + 10;
      const yrs: number[] = [];
      for (let year = fromYear; year <= toYear; year++) {
        yrs.push(year);
      }
      return yrs;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [(props as any).fromYear, (props as any).toYear]);

    const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      const month = parseInt(e.target.value, 10);
      goToMonth(new Date(displayMonth.getFullYear(), month));
    };

    const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      const year = parseInt(e.target.value, 10);
      goToMonth(new Date(year, displayMonth.getMonth()));
    };

    const handlePreviousMonth = () => {
      const currentMonth = displayMonth.getMonth();
      const currentYear = displayMonth.getFullYear();
      if (currentMonth === 0) {
        goToMonth(new Date(currentYear - 1, 11));
      } else {
        goToMonth(new Date(currentYear, currentMonth - 1));
      }
    };

    const handleNextMonth = () => {
      const currentMonth = displayMonth.getMonth();
      const currentYear = displayMonth.getFullYear();
      if (currentMonth === 11) {
        goToMonth(new Date(currentYear + 1, 0));
      } else {
        goToMonth(new Date(currentYear, currentMonth + 1));
      }
    };

    return (
      <div className="relative flex items-center justify-center space-x-2 pt-1">
        <button
          type="button"
          onClick={handlePreviousMonth}
          className="flex h-5 w-5 items-center p-0 opacity-75 hover:opacity-100"
        >
          <ChevronLeftIcon className="h-5 w-5" />
        </button>

        <select
          className="rounded-md border border-input bg-background py-1 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary"
          onChange={handleMonthChange}
          value={displayMonth.getMonth()}
        >
          {months.map((month, index) => (
            <option key={index} value={index}>
              {getCapitalizedMonth(format(month, 'LLLL', { locale }))}
            </option>
          ))}
        </select>

        <select
          className="rounded-md border border-input bg-background py-1 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary"
          onChange={handleYearChange}
          value={displayMonth.getFullYear()}
        >
          {years.map(year => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>

        <button
          type="button"
          onClick={handleNextMonth}
          className="flex h-5 w-5 items-center p-0 opacity-75 hover:opacity-100"
        >
          <ChevronRightIcon className="h-5 w-5" />
        </button>
      </div>
    );
  };

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      locale={locale}
      // hide default nav since CustomMonthCaption provides its own prev/next buttons
      hideNavigation
      className={cn('p-3', className)}
      classNames={{
        // v9 renamed class keys
        months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
        month: 'space-y-4',
        month_caption: 'flex justify-center pt-1 relative items-center',
        caption_label: 'text-sm font-medium',
        nav: 'space-x-1 flex items-center',
        button_previous: cn(
          buttonVariants({ variant: 'outline' }),
          'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100',
        ),
        button_next: cn(
          buttonVariants({ variant: 'outline' }),
          'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100',
        ),
        month_grid: 'w-full border-collapse space-y-1',
        weekdays: 'flex',
        weekday: 'text-muted-foreground rounded-md w-8 font-normal text-[0.8rem] text-center',
        week: 'flex w-full mt-2',
        day: cn(
          'relative p-0 text-center text-sm focus-within:relative focus-within:z-20',
          props.mode === 'range'
            ? '[&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md'
            : '[&:has([aria-selected])]:rounded-md',
        ),
        day_button: cn(
          buttonVariants({ variant: 'ghost' }),
          'h-8 w-8 p-0 font-normal aria-selected:opacity-100',
        ),
        // modifier classes (applied to the day td element)
        range_start: 'day-range-start',
        range_end: 'day-range-end',
        selected:
          'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground rounded-md',
        today: 'bg-accent text-accent-foreground',
        outside: 'text-muted-foreground opacity-50',
        disabled: 'text-muted-foreground opacity-50',
        range_middle: 'aria-selected:bg-accent aria-selected:text-accent-foreground',
        hidden: 'invisible',
        ...classNames,
      }}
      components={{
        MonthCaption: CustomMonthCaption as any,
      }}
      weekStartsOn={props.weekStartsOn || 1}
      formatters={{
        formatWeekdayName: (date: Date) => format(date, 'EE', { locale }),
      }}
      {...props}
    />
  );
}

Calendar.displayName = 'Calendar';

export { Calendar };
